// Denial procedure for a confirmed ACCESS_DENIED state
// (docs/amends/user-access-restriction/tasks.md, P5-4 through P5-7). Does NOT
// apply to ACCESS_CHECK_FAILED - that state is not a confirmed policy denial
// (remediation-plan.md §4.6) and must not force a logout.
//
// Does NOT call msalInstance.logoutRedirect(). Three independent, standards-
// based attempts to suppress Microsoft's "Pick an account" chooser on this
// Entra External ID (CIAM) tenant's end_session endpoint all failed to work
// live, even though each is individually well-documented and correctly wired:
//   1. `account` (meant to attach id_token_hint automatically) - still showed
//      the chooser.
//   2. `logoutHint` derived from every claim MSAL/Entra commonly uses for it
//      (login_hint, username, preferred_username, upn, email, emails[]) -
//      moot anyway, since this tenant's ID token turned out to carry none of
//      them (confirmed via a full claims dump: only aud/iss/name/oid/sid/sub
//      /tid/etc.).
//   3. `idTokenHint` (the raw ID token itself, via an explicit silent
//      reacquisition) - the standards-based (OIDC) mechanism that doesn't
//      depend on any claim being present at all - still showed the chooser.
// With the IdP round-trip itself unable to avoid an unwanted UI prompt on
// this tenant regardless of parameters, the fix is to not do that round-trip
// for this flow: `clearCache()` removes the account's session from MSAL's own
// (local, browser-side) cache with no network call and no redirect at all
// (confirmed by reading msal-browser's SilentCacheClient.logout /
// clearCacheOnLogout source), then a plain navigation takes the user to
// /access-denied directly. Trade-off: the user's session at the identity
// provider itself is left alive (they are not "signed out of Entra ID" at the
// browser/IdP level) - only the app's own local session is cleared. A future
// visit re-runs the full access check from scratch (silent SSO may or may not
// succeed, but either way lands back on the correct VERIFYING_ACCESS/
// ACCESS_DENIED outcome), which satisfies the practical goal (no stale local
// session, no ability to reuse this denied session's cached data) without
// depending on IdP UI behaviour this app doesn't control.

const APP_OWNED_SESSION_STORAGE_KEYS = ['prevPath'];

export const clearAppOwnedClientState = () => {
    try {
        localStorage.clear();
    } catch (err) {
        // Storage may be unavailable (private browsing, etc.) - not fatal.
    }

    APP_OWNED_SESSION_STORAGE_KEYS.forEach((key) => {
        try {
            sessionStorage.removeItem(key);
        } catch (err) {
            // ignore
        }
    });
};

/**
 * Runs the full confirmed-denial procedure: purge the Apollo cache, clear
 * app-owned client storage, clear the account's local MSAL session (no IdP
 * round-trip - see file header), then navigate to the public /access-denied
 * route (never back inside MsalAuthenticationTemplate's protected tree - see
 * PublicAccessDeniedRoute.js for why that matters).
 */
export const runAccessDenialProcedure = async ({ apolloClient, reasonCode, correlationId, msalInstance, account }) => {
    try {
        await apolloClient.clearStore();
    } catch (err) {
        // Proceed with cleanup even if the cache purge fails - a denied
        // identity must not keep an active session regardless.
    }

    clearAppOwnedClientState();

    try {
        await msalInstance.clearCache(account ? { account } : undefined);
    } catch (err) {
        // Not fatal - proceed to the denial screen regardless.
    }

    // Passed as query params since this is a full page navigation - all
    // in-memory React/context state (including correlationId from the
    // backend's own access-control log line) is otherwise lost.
    const params = new URLSearchParams();
    if (reasonCode) params.set('reason', reasonCode);
    if (correlationId) params.set('correlationId', correlationId);
    const query = params.toString();

    window.location.assign(`/access-denied${query ? `?${query}` : ''}`);
};
